import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckBadgeIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import ActionMenu from "../Menus/ActionMenu";
import { resolvePrimaryLocationDisplay } from "../../utils/myVendorsMapper";
import { getVendorDisplayType } from "../../utils/myVendorsConstants";
import { vendorTypeColors } from "../../utils/constants";
import {
  filterAndSortKnownServiceNames,
  resolveServiceBadgePresentation,
} from "../../utils/serviceCategoryBadgeDisplay";
import { StickyActionsTd, StickyActionsTh } from "../../components/StickyActionsCells";
import {
  clickableRowClass,
  shouldIgnoreRowClick,
} from "../../utils/gridTableUx";
import GridOrgNameLink from "../common/GridOrgNameLink";
import { useGridOrgNameNavigation } from "../../custom-hooks/useGridOrgNameNavigation";

function occurrences(provider, word) {
  const string = provider.documents || "";
  if (!string) {
    if (word === "RFI") return provider.rfi_count || 0;
    else if (word === "Audit") return provider.assessment_count || 0;
  }
  return string.split(word).length - 1;
}

function ProvidersListGrid({
  providers,
  selectedOptions,
  setSelectedOptions,
  sortColumn,
  sortDirection,
  onSort,
  serviceColors,
  hideRequestActions = false,
  canSelectVendors = false,
  canManageMyVendors = false,
  getMyVendorMembership = null,
  onAddToMyVendors = null,
  onRemoveFromMyVendors = null,
  onAddToHidden = null,
  onUnhide = null,
  onCreateList = null,
  onAddToList = null,
  isBookmarked = null,
  onToggleBookmark = null,
  isBookmarkPending = null,
  hideVqq = false,
}) {
  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const navigate = useNavigate();
  const { navigateToHref, vendorHrefForRow } = useGridOrgNameNavigation();
  const canShowSelection = canSelectVendors;

  const handleProviderClick = (provider) => {
    navigate(`/provider/${provider.id}/overview`);
    // Scroll to top when navigating to provider profile
    window.scrollTo(0, 0);
  };

  // Helper to normalize provider name for robust comparison
  const normalizeName = (name) =>
    (name || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");

  // Get a set of normalized selected provider names
  const selectedNamesSet = new Set(
    (selectedOptions || []).map((p) => normalizeName(p.name))
  );

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedOptions?.length > 0 && selectedOptions?.length < providers.length;
    setChecked(selectedOptions?.length === providers.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) checkbox.current.indeterminate = isIndeterminate;
  }, [selectedOptions, providers.length]);

  function toggleAll() {
    const shouldDeselectAll = checked || indeterminate;

    if (shouldDeselectAll) {
      // Deselect all providers
      setSelectedOptions([]);
    } else {
      // Copy so selection state never shares the React Query cache array reference
      setSelectedOptions([...(providers || [])]);
    }
  }

  function handleSelectProvider(e, provider) {
    const normName = normalizeName(provider.name);
    if (e.target.checked) {
      // Add if not already present
      if (!selectedNamesSet.has(normName)) {
        setSelectedOptions([...selectedOptions, provider]);
      }
    } else {
      // Remove by normalized name
      setSelectedOptions(
        selectedOptions.filter((p) => normalizeName(p.name) !== normName)
      );
    }
  }

  function handleCompareButtonClick() {
    navigate("/Provider/Compare", {
      state: { selectedVendors: selectedOptions },
    });
  }

  const toggleRowExpansion = (providerName) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(providerName)) {
      newExpanded.delete(providerName);
    } else {
      newExpanded.add(providerName);
    }
    setExpandedRows(newExpanded);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg
          className="ml-1 h-4 w-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    if (sortDirection === "asc") {
      return (
        <svg
          className="ml-1 h-4 w-4 text-indigo-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 15l7-7 7 7"
          />
        </svg>
      );
    }

    return (
      <svg
        className="ml-1 h-4 w-4 text-indigo-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  return (
    <div className="flow-root">
      <div className="overflow-x-auto">
        <div className="min-w-full py-0 align-middle">
          <div className="relative">
            {selectedOptions?.length > 0 && null}
            <table className="min-w-full table-auto w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {canShowSelection && (
                    <th
                      scope="col"
                      className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900 w-12"
                    >
                      <div className="group grid size-5 grid-cols-1">
                        <input
                          type="checkbox"
                          ref={checkbox}
                          className="col-start-1 row-start-1 size-5 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                          checked={checked}
                          onChange={toggleAll}
                        />
                        <svg
                          viewBox="0 0 16 16"
                          fill="white"
                          className="pointer-events-none col-start-1 row-start-1 size-5 opacity-0 group-has-[input:checked]:opacity-100"
                        >
                          <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                        </svg>
                        <svg
                          viewBox="0 0 16 16"
                          fill="white"
                          className="pointer-events-none col-start-1 row-start-1 size-5 opacity-0 group-has-[input:indeterminate]:opacity-100"
                        >
                          <path
                            d="M4 8h8"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    </th>
                  )}
                  <th
                    scope="col"
                    className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[160px]"
                  >
                    <button
                      className="inline-flex items-center group"
                      onClick={() => onSort("name")}
                    >
                      <span>Name</span>
                      <span
                        className={
                          sortColumn !== "name"
                            ? "group-hover:[&>svg]:text-indigo-600"
                            : ""
                        }
                      >
                        {renderSortIcon("name")}
                      </span>
                    </button>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[220px]"
                  >
                    Services
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[120px]"
                  >
                    <button
                      className="inline-flex items-center group"
                      onClick={() => onSort("type")}
                    >
                      <span>Type</span>
                      <span
                        className={
                          sortColumn !== "type"
                            ? "group-hover:[&>svg]:text-indigo-600"
                            : ""
                        }
                      >
                        {renderSortIcon("type")}
                      </span>
                    </button>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[140px]"
                  >
                    <button
                      className="inline-flex items-center group"
                      onClick={() => onSort("location")}
                    >
                      <span>Primary Location</span>
                      <span
                        className={
                          sortColumn !== "location"
                            ? "group-hover:[&>svg]:text-indigo-600"
                            : ""
                        }
                      >
                        {renderSortIcon("location")}
                      </span>
                    </button>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[120px]"
                  >
                    Documents
                  </th>
                  <StickyActionsTh tone="gray" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {providers?.map((provider) => {
                  const isSelected = selectedNamesSet.has(
                    normalizeName(provider.name)
                  );
                  return (
                  <tr
                    key={provider.id || provider.name}
                    className={clickableRowClass({ selected: isSelected })}
                    onClick={(e) => {
                      if (
                        shouldIgnoreRowClick(e.target)
                      ) {
                        return;
                      }
                      handleProviderClick(provider);
                    }}
                  >
                    {canShowSelection && (
                      <td className="relative px-4 py-4 w-12">
                        {selectedNamesSet.has(normalizeName(provider.name)) && (
                          <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" />
                        )}
                        <div className="group grid size-5 grid-cols-1">
                          <input
                            type="checkbox"
                            className="col-start-1 row-start-1 size-5 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            value={provider.name}
                            checked={selectedNamesSet.has(
                              normalizeName(provider.name)
                            )}
                            onChange={(e) => {
                              handleSelectProvider(e, provider);
                            }}
                          />
                          <svg
                            viewBox="0 0 16 16"
                            fill="white"
                            className="pointer-events-none col-start-1 row-start-1 size-5 opacity-0 group-has-[input:checked]:opacity-100"
                          >
                            <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                          </svg>
                        </div>
                      </td>
                    )}
                    <td className="whitespace-nowrap px-4 py-4 text-sm min-w-[160px]">
                      <GridOrgNameLink
                        name={provider.name}
                        href={vendorHrefForRow({
                          provider_id: provider.id,
                          provider_visible_in_discovery: true,
                        })}
                        onNavigate={(href) => {
                          navigateToHref(href);
                          window.scrollTo(0, 0);
                        }}
                        className="text-sm font-semibold whitespace-normal"
                      />
                    </td>
                    <td className="py-4 px-4 text-sm font-medium min-w-[220px] text-wrap">
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          const sortedServices = filterAndSortKnownServiceNames(
                            provider.provider_capabilities?.map(
                              (p) => p.rfi_name
                            ) || [],
                            serviceColors
                          );

                          const isExpanded = expandedRows.has(provider.name);
                          const maxVisibleServices = 6; // Show up to 6 services before collapsing
                          const visibleServices = isExpanded
                            ? sortedServices
                            : sortedServices.slice(0, maxVisibleServices);
                          const hiddenCount =
                            sortedServices.length - maxVisibleServices;

                          return (
                            <>
                              {visibleServices?.map((s) => {
                                const { trimmed, displayName, color } =
                                  resolveServiceBadgePresentation(
                                    s,
                                    serviceColors
                                  );
                                const colorStyle = color
                                  ? {
                                      backgroundColor: color.bgColor,
                                      color: color.textColor,
                                      boxShadow: `inset 0 0 0 1px ${color.borderColor}1A`,
                                    }
                                  : {};
                                return (
                                  <span
                                    key={trimmed}
                                    style={colorStyle}
                                    className={`inline-flex items-center rounded-md ${color.bgColor} px-2 py-1 text-xs font-medium ${color.textColor} ring-1 ring-inset ${color.borderColor}`}
                                    title={displayName}
                                  >
                                    {displayName}
                                  </span>
                                );
                              })}
                              {!isExpanded && hiddenCount > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRowExpansion(provider.name);
                                  }}
                                  className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 hover:bg-gray-200 transition-colors"
                                >
                                  +{hiddenCount} more
                                </button>
                              )}
                              {isExpanded &&
                                sortedServices.length > maxVisibleServices && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleRowExpansion(provider.name);
                                    }}
                                    className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 hover:bg-gray-200 transition-colors"
                                  >
                                    Show less
                                  </button>
                                )}
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 min-w-[120px] text-wrap">
                      <span className="flex items-center gap-2">
                        {provider.provider_type &&
                          vendorTypeColors[provider.provider_type] && (
                            <span
                              className={`inline-block min-w-2 w-2 h-2 rounded-full ${
                                vendorTypeColors[provider.provider_type]
                              }`}
                            />
                          )}
                        <span className="min-w-0 whitespace-normal break-normal">
                          {getVendorDisplayType(provider.provider_type)}
                        </span>
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500 min-w-[140px]">
                      {resolvePrimaryLocationDisplay(provider, provider?.locations || []) || (
                        <span className="text-gray-400">–</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500 min-w-[120px] text-center">
                      {!hideVqq && (
                        <div className="flex items-center">
                          <CheckCircleIcon className="w-[20px] h-[20px] text-indigo-700" />
                          <span className="inline-block pl-2">
                            {occurrences(provider, "RFI")} VQQs
                          </span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <CheckBadgeIcon className="w-[20px] h-[20px] text-rose-700" />
                        <span className="inline-block pl-2">
                          {occurrences(provider, "Audit")} Audits
                        </span>
                      </div>
                    </td>
                    <StickyActionsTd align="right" selected={isSelected}>
                      <ActionMenu
                        provider={provider}
                        urlLink={"/provider/overview"}
                        onActionClick={() => handleProviderClick(provider)}
                        hideRequestActions={hideRequestActions}
                        canManageMyVendors={canManageMyVendors}
                        myVendorMembership={
                          getMyVendorMembership
                            ? getMyVendorMembership(provider.id)
                            : null
                        }
                        onAddToMyVendors={onAddToMyVendors}
                        onRemoveFromMyVendors={onRemoveFromMyVendors}
                        onAddToHidden={onAddToHidden}
                        onUnhide={onUnhide}
                        onCreateList={onCreateList}
                        onAddToList={onAddToList}
                        isBookmarked={isBookmarked ? isBookmarked(provider.id) : false}
                        isBookmarkPending={
                          isBookmarkPending ? isBookmarkPending(provider.id) : false
                        }
                        onToggleBookmark={onToggleBookmark}
                      />
                    </StickyActionsTd>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProvidersListGrid;
